import { FEATURE_FLAGS } from '@mondaygpt/constants'
import { useCallback } from 'react'
import { useFeatureGateState } from './use-feature-gate'

/**
 * Rollout flags for social platforms that are not GA. Instagram / Facebook /
 * LinkedIn have no flag and always return true.
 *
 * Fail closed until the logged-in identity's flags have settled — a gated
 * platform (TikTok, X, YouTube, Pinterest) must not flash for a user who is
 * not in the rollout. Same settled-identity contract as `useEngagementGate` /
 * referrals.
 *
 * Accepts both LivePlatform keys (`youtube_shorts`, `x`) and OAuth provider
 * keys (`youtube`, `twitter`) so Integrations and the post surfaces share one
 * check.
 */
export function usePlatformRolloutEnabled(): (platform: string) => boolean {
  const tiktok = useFeatureGateState(FEATURE_FLAGS.enableTiktok)
  const youtube = useFeatureGateState(FEATURE_FLAGS.enableYoutube)
  const pinterest = useFeatureGateState(FEATURE_FLAGS.enablePinterest)
  const x = useFeatureGateState(FEATURE_FLAGS.enableX)

  return useCallback(
    (platform: string) => {
      switch (platform) {
        case 'tiktok':
          return tiktok.settled && tiktok.enabled
        case 'youtube':
        case 'youtube_shorts':
          return youtube.settled && youtube.enabled
        case 'pinterest':
          return pinterest.settled && pinterest.enabled
        case 'x':
        case 'twitter':
          return x.settled && x.enabled
        default:
          return true
      }
    },
    [
      tiktok.settled,
      tiktok.enabled,
      youtube.settled,
      youtube.enabled,
      pinterest.settled,
      pinterest.enabled,
      x.settled,
      x.enabled,
    ]
  )
}
